// src/taskpane/officeAsync.js

export async function getComposeContext() {
  const office = window.Office;
  const item = office?.context?.mailbox?.item;

  // Browser/dev fallback so taskpane can be tested outside Outlook.
  if (!item) {
    console.warn("Office mailbox item not available. Using browser dev fallback.");

    return {
      provider: "outlook-addin-browser-dev",
      subject: "",
      to: ["test@example.com"],
      cc: [],
      sender: {
        email: "test@example.com",
        fullName: "Test User",
        firstName: "Test",
        lastName: "User"
      },
      draft: "",
      threadText: "",
      compose: {
        signatureExists: false,
        existingBodyText: ""
      }
    };
  }

  const subject = await getSubjectAsync(item);
  const bodyText = await getBodyTextAsync(item);
  const toPeople = await getRecipientsAsync(item.to);
  const ccPeople = await getRecipientsAsync(item.cc);

  const firstTo = toPeople[0] || {
    email: "",
    fullName: "",
    firstName: "",
    lastName: ""
  };

  return {
    provider: "outlook-addin",
    subject,
    to: toPeople.map(p => p.email).filter(Boolean),
    cc: ccPeople.map(p => p.email).filter(Boolean),
    toPeople,
    ccPeople,
    sender: firstTo,
    draft: bodyText,
    threadText: bodyText,
    compose: {
      signatureExists: hasSignatureLikeText(bodyText),
      existingBodyText: bodyText
    }
  };
}

function textToEmailHtml(text = "") {
  return String(text || "")
    .replace(/\r/g, "")
    .split(/\n{2,}/)
    .map(function (paragraph) {
      return "<p>" + escapeHtml(paragraph).replace(/\n/g, "<br>") + "</p>";
    })
    .join("");
}

function escapeHtml(value = "") {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function getSubjectAsync(item) {
  const office = window.Office;

  return new Promise((resolve) => {
    if (!office || !item?.subject?.getAsync) return resolve("");

    item.subject.getAsync((result) => {
      resolve(
        result.status === office.AsyncResultStatus.Succeeded
          ? result.value || ""
          : ""
      );
    });
  });
}

function getBodyTextAsync(item) {
  const office = window.Office;

  return new Promise((resolve) => {
    if (!office || !item?.body?.getAsync) return resolve("");

    item.body.getAsync(office.CoercionType.Text, (result) => {
      resolve(
        result.status === office.AsyncResultStatus.Succeeded
          ? result.value || ""
          : ""
      );
    });
  });
}

function getRecipientsAsync(recipientsApi) {
  const office = window.Office;

  return new Promise((resolve) => {
    if (!office || !recipientsApi?.getAsync) return resolve([]);

    recipientsApi.getAsync((result) => {
      if (result.status !== office.AsyncResultStatus.Succeeded) {
        return resolve([]);
      }

      const people = (result.value || [])
        .map((r) => {
          const email = String(r.emailAddress || "").toLowerCase().trim();
          const fullName = cleanName(r.displayName || "") || inferNameFromEmail(email);

          return {
            email,
            fullName,
            firstName: getFirstName(fullName) || inferFirstNameFromEmail(email),
            lastName: getLastName(fullName)
          };
        })
        .filter((p) => p.email);

      resolve(people);
    });
  });
}

export async function setSubjectIfEmpty(subject = "") {
  const office = window.Office;
  const item = office?.context?.mailbox?.item;

  if (!office || !item || !subject) return;

  const current = await getSubjectAsync(item);
  if (String(current || "").trim()) return;

  if (!item.subject?.setAsync) return;

  return new Promise((resolve) => {
    item.subject.setAsync(subject, () => resolve());
  });
}

export async function setBodyHtml(html = "") {
  const office = window.Office;
  const item = office?.context?.mailbox?.item;

  if (!office || !item || !html) return;

  return new Promise((resolve, reject) => {
    item.body.setSelectedDataAsync(
      html,
      { coercionType: office.CoercionType.Html },
      (result) => {
        if (result.status === office.AsyncResultStatus.Succeeded) {
          resolve();
        } else {
          reject(new Error(result.error?.message || "Could not insert draft."));
        }
      }
    );
  });
}

function cleanName(value = "") {
  const v = String(value || "")
    .replace(/<[^>]+>/g, "")
    .replace(/["']/g, "")
    .replace(/\s+/g, " ")
    .trim();

  if (!v || v.includes("@")) return "";
  return v;
}

function getFirstName(fullName = "") {
  const clean = cleanName(fullName);
  return clean ? clean.split(/\s+/)[0] || "" : "";
}

function getLastName(fullName = "") {
  const clean = cleanName(fullName);
  return clean ? clean.split(/\s+/).slice(1).join(" ") : "";
}

function inferNameFromEmail(email = "") {
  const local = String(email || "")
    .split("@")[0]
    .replace(/[._-]+/g, " ")
    .replace(/\d+/g, "")
    .replace(/\s+/g, " ")
    .trim();

  if (!local) return "";

  return local
    .split(" ")
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function inferFirstNameFromEmail(email = "") {
  return inferNameFromEmail(email).split(/\s+/)[0] || "";
}

function hasSignatureLikeText(text = "") {
  const tail = String(text || "").toLowerCase().slice(-800);

  return (
    tail.includes("--") ||
    tail.includes("regards,") ||
    tail.includes("best,") ||
    tail.includes("kind regards,") ||
    tail.includes("sent from my iphone")
  );
}

export async function getSelectedTextAsync() {
  const office = window.Office;
  const item = office?.context?.mailbox?.item;

  if (!office || !item?.getSelectedDataAsync) {
    console.warn("getSelectedDataAsync is not available on this Outlook item.");
    return "";
  }

  return new Promise((resolve) => {
    item.getSelectedDataAsync(
      office.CoercionType.Text,
      { asyncContext: null },
      function (result) {
        if (result.status !== office.AsyncResultStatus.Succeeded) {
          console.warn("getSelectedDataAsync failed:", result.error);
          resolve("");
          return;
        }

        const value = result.value;

        console.log("Selected data raw result:", value);

        if (typeof value === "string") {
          resolve(value.trim());
          return;
        }

        if (value && typeof value.data === "string") {
          resolve(value.data.trim());
          return;
        }

        if (value && typeof value.value === "string") {
          resolve(value.value.trim());
          return;
        }

        resolve("");
      }
    );
  });
}

export async function replaceSelectedTextAsync(text = "") {
  const office = window.Office;
  const item = office?.context?.mailbox?.item;

  if (!office || !item || !text) {
    return;
  }

  const html = textToEmailHtml(text);

  return new Promise((resolve, reject) => {
    const callback = function (result) {
      if (result.status === office.AsyncResultStatus.Succeeded) {
        resolve();
      } else {
        reject(new Error(result.error?.message || "Could not replace selected text."));
      }
    };

    if (item.setSelectedDataAsync) {
      item.setSelectedDataAsync(
        html,
        { coercionType: office.CoercionType.Html },
        callback
      );
      return;
    }

    if (item.body?.setSelectedDataAsync) {
      item.body.setSelectedDataAsync(
        html,
        { coercionType: office.CoercionType.Html },
        callback
      );
      return;
    }

    reject(new Error("Selected text replacement is not available in this Outlook client."));
  });
}

export async function replaceBodyHtmlAsync(html = "") {
  const office = window.Office;
  const item = office?.context?.mailbox?.item;

  if (!office || !item?.body?.setAsync || !html) {
    return;
  }

  return new Promise((resolve, reject) => {
    item.body.setAsync(
      html,
      { coercionType: office.CoercionType.Html },
      function (result) {
        if (result.status === office.AsyncResultStatus.Succeeded) {
          resolve();
        } else {
          reject(new Error(result.error?.message || "Could not replace email body."));
        }
      }
    );
  });
}

export function extractEditableDraftOnly(text = "") {
  let value = String(text || "").replace(/\r/g, "").trim();

  if (!value) return "";

  const separators = [
    /^From:\s.+$/im,
    /^Sent:\s.+$/im,
    /^On .+ wrote:$/im,
    /^-----Original Message-----$/im,
    /^_{5,}$/m,
    /^-{5,}$/m
  ];

  let cutIndex = -1;

  for (let i = 0; i < separators.length; i += 1) {
    const match = value.match(separators[i]);
    if (match && typeof match.index === "number") {
      if (cutIndex === -1 || match.index < cutIndex) {
        cutIndex = match.index;
      }
    }
  }

  if (cutIndex >= 0) {
    value = value.slice(0, cutIndex).trim();
  }

  return value;
}


export async function getComposeBodyTextAsync() {
  const office = window.Office;
  const item = office?.context?.mailbox?.item;

  if (!office || !item?.body?.getAsync) {
    return "";
  }

  return new Promise((resolve) => {
    item.body.getAsync(
      office.CoercionType.Text,
      function (result) {
        if (result.status === office.AsyncResultStatus.Succeeded) {
          resolve(String(result.value || "").trim());
        } else {
          console.warn("body.getAsync failed:", result.error);
          resolve("");
        }
      }
    );
  });
}